import { ReusableWizard, useReusableWizard } from "@oysterhr/feature-components"
import { Alert, Button, Icon, Link, LoadingSpinner, Text, Toast, useToast } from "@oysterhr/ui"
import { useAccountAspect } from "@oysterhr/identity/AccountAspectContext"
import { useNavigate, useParams } from "react-router-dom"
import { companyPaymentMethodsPath } from "@oysterhr/router"
import {
  PaymentMethodsCustomerBankAccountSepaType,
  PaymentMethodState,
  PaymentMethodStripeSepa,
} from "@oysterhr/schema"
import { useTranslation } from "react-i18next"
import { useRef, useState } from "react"
import { useAuthorizePaymentMethod } from "../../../../hooks/useAuthorizeDirectDebitOnPaymentMethod"
import { useNewCompanyPaymentMethods } from "../../../../hooks/useNewCompanyPaymentMethods"
import { PaymentMethodCategoryEnumType } from "../../../../enums"
import { formatAddressToSingleLine } from "../../../../utils"
import { ConfirmMandateSignerModal } from "../../../../modals/ConfirmMandateSignerModal"
import { GenerateMandateErrorModal } from "../../../../modals/GenerateMandateErrorModal"

const FieldCell = ({ name, value }: { name: string; value?: string | null }) => {
  if (!value) return null

  return (
    <div className="flex flex-col">
      <Text className="font-semibold">{name}</Text>
      <Text>{value}</Text>
    </div>
  )
}

export const AuthorizeDebitStep = () => {
  // Hooks
  const { t } = useTranslation("company_interface/CompanyPaymentMethodsPage/SepaFlow", {
    keyPrefix: "authorizationStep",
  })
  const { paymentMethodType } = useParams()
  const { accountAspect: companyId } = useAccountAspect()
  const navigate = useNavigate()
  const toast = useToast()
  const { goToPrevious } = useReusableWizard()
  const [isLoading, setIsLoading] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const generationAttempts = useRef(0)

  const [isConfirmMandateModalOpen, setIsConfirmMandateModalOpen] = useState(false)

  // Number of maximum queries to get mandate sign url
  const MAX_RETRY_LIMIT = 5
  const POLL_INTERVAL = 5000

  // GraphQL
  const [authorize] = useAuthorizePaymentMethod()
  const { data, startPolling, stopPolling } = useNewCompanyPaymentMethods({
    variables: { companyId },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onCompleted: result => {
      // We only poll for FTE payment method
      if (!isFTEPaymentMethod) return stopPolling()

      generationAttempts.current += 1

      const mandateSignURL = result.company?.customerBankAccount?.mandate?.signerRecipientViewUrl
      const paymentMethodState = result?.company?.customerBankAccount?.state
      // Check if mandate has been generated and if user has authorized payment method (clicked confirm)
      if (mandateSignURL && paymentMethodState === PaymentMethodState.AuthorizedByUser) {
        window.location.href = mandateSignURL
      } else if (generationAttempts.current >= MAX_RETRY_LIMIT) {
        stopPolling()
        setIsLoading(false)
        setShowErrorModal(true)
      }
    },
  })

  // Derived state
  const paymentMethod = data?.company?.paymentMethod as PaymentMethodStripeSepa | undefined
  const customerBankAccount = data?.company?.customerBankAccount as
    | PaymentMethodsCustomerBankAccountSepaType
    | undefined

  const isFTEPaymentMethod = paymentMethodType === PaymentMethodCategoryEnumType.Fte
  const selectedPaymentMethod = isFTEPaymentMethod ? customerBankAccount : paymentMethod

  // Actions
  const onAuthorize = () => {
    const paymentMethodId = isFTEPaymentMethod ? customerBankAccount?.id : paymentMethod?.id
    authorize({
      variables: { authorized: true, paymentMethodId: paymentMethodId ?? "" },
      onCompleted: () => {
        if (!isFTEPaymentMethod) {
          toast({
            render: (
              <Toast variation="success">
                <Toast.Title>{t("toast.title")}</Toast.Title>
                <Toast.Content>{t("toast.content")}</Toast.Content>
              </Toast>
            ),
          })
          return navigate(companyPaymentMethodsPath(companyId))
        }

        if (customerBankAccount?.userNotSigner) {
          toast({
            render: (
              <Toast variation="success">
                <Toast.Title>{t("toast.mandateSignerToast.title")}</Toast.Title>
                <Toast.Content>{t("toast.mandateSignerToast.content")}</Toast.Content>
              </Toast>
            ),
          })
          return navigate(companyPaymentMethodsPath(companyId))
        }

        const mandateSignURL = customerBankAccount?.mandate?.signerRecipientViewUrl
        if (mandateSignURL) {
          window.location.href = mandateSignURL
        } else {
          startPolling(POLL_INTERVAL)
          setIsLoading(true)
          toast({
            render: (
              <Toast variation="info">
                <Toast.Title>{t("toast.mandate.title")}</Toast.Title>
                <Toast.Content>{t("toast.mandate.content")}</Toast.Content>
              </Toast>
            ),
          })
        }
      },
    })
  }

  return (
    <ReusableWizard.Step title={t("title")}>
      <div className="space-y-4 text-left">
        <Text className="mt-8">{t("content")}</Text>
        {isFTEPaymentMethod && (
          <Alert variation="info">
            <Alert.Icon />
            <Alert.Content>{t("alert")}</Alert.Content>
          </Alert>
        )}
        {selectedPaymentMethod ? (
          <div className="space-y-4">
            <FieldCell
              name={t("accountHolderName")}
              value={selectedPaymentMethod.accountHolderName}
            />
            <FieldCell
              name={t("accountIBAN")}
              value={`****${selectedPaymentMethod.accountNumber.slice(-4)}`}
            />
            <FieldCell
              name={t("accountAddress")}
              value={formatAddressToSingleLine(selectedPaymentMethod.billingAddress)}
            />
          </div>
        ) : (
          <LoadingSpinner />
        )}
        <div className="mt-8 flex space-x-8">
          {isFTEPaymentMethod && (
            <Link as="button" onClick={goToPrevious} className="text-gray-400 no-underline">
              <Icon name="ChevronLeft" size="sm" />
              {t("btnBack")}
            </Link>
          )}
          <Button
            onClick={() => {
              if (customerBankAccount?.userNotSigner) {
                setIsConfirmMandateModalOpen(true)
              } else {
                onAuthorize()
              }
            }}
            isLoading={isLoading}
          >
            {t("btnConfirm")}
          </Button>
        </div>
      </div>

      {customerBankAccount ? (
        <ConfirmMandateSignerModal
          isOpen={isConfirmMandateModalOpen}
          values={{
            signerEmail: customerBankAccount?.email as string,
            isUserSigner: !customerBankAccount?.userNotSigner,
          }}
          onRequestClose={() => setIsConfirmMandateModalOpen(false)}
          onConfirm={() => onAuthorize()}
          onChangeSigner={() => goToPrevious()}
        />
      ) : null}

      <GenerateMandateErrorModal
        isOpen={showErrorModal}
        companyId={companyId}
        paymentMethod={customerBankAccount}
        onClose={() => setShowErrorModal(false)}
      />
    </ReusableWizard.Step>
  )
}
